import Phaser from 'phaser';
import { WALL_X, WALL_WIDTH, COLORS, ENEMY_TYPES, GAME_WIDTH, ENEMY_MIN_Y, ENEMY_MAX_Y, BOSS_DEFINITIONS } from '../config';
import { EnemyConfig, EnemyType } from '../types';
import { StickFigureGraphics } from '../rendering/StickFigureGraphics';
import { Wall } from './Wall';
import { EnemyArrow } from './EnemyArrow';
import { FloatingTextManager } from '../ui/systems/FloatingTextManager';

export class Enemy {
  scene: Phaser.Scene;
  x: number;
  y: number;
  hp: number;
  maxHP: number;
  speed: number;
  baseSpeed: number;
  damage: number;
  attackInterval: number;
  goldReward: number;
  isAttacking: boolean;
  isDead: boolean;
  isProcessed: boolean = false;
  graphics: Phaser.GameObjects.Graphics;
  hpBarGfx: Phaser.GameObjects.Graphics;
  sprite: Phaser.GameObjects.Sprite;
  walkPhase: number;
  attackTimer: number; // Delta-based timer
  hitFlashTimer: number;
  enemyType: EnemyType;
  isBoss: boolean;
  scale: number;

  // Slow effect
  slowMultiplier: number = 1;
  slowTimer: number = 0; // Delta-based timer

  // Swarm effect
  isSwarmed: boolean = false;
  swarmTimer: number = 0;
  swarmBees: { x: number, y: number, angle: number }[] = [];

  // Poison effect
  poisonDps: number = 0;
  poisonTimer: number = 0;
  private poisonTickTimer: number = 0;

  // Fire DoT
  fireDps: number = 0;
  fireTimer: number = 0;
  private fireTickTimer: number = 0;

  // Shaman healing
  healPercent: number = 0; // Fraction of target's maxHP per pulse
  healTimer: number = 0; // Delta-based timer

  // Attack range: 0 = melee (walk to wall), >0 = stop that many px from wall
  attackRange: number = 0;
  // Miss chance for ranged enemies
  missChance: number = 0;

  // Ranged damage reduction (shield)
  rangedDamageReduction: number = 0;

  // CC effects
  stunTimer: number = 0;
  disarmTimer: number = 0;

  // Shaman wander target
  private wanderTarget: { x: number, y: number } | null = null;
  private wanderTimer: number = 0;

  // Nest carrier — spawns swarmlings periodically
  nestSpawnTimer: number = 0;
  nestSpawnInterval: number = 3000; // Spawn every 3s
  nestChildCount: number = 0; // Track live children
  static readonly NEST_MAX_CHILDREN = 6;

  // Splitter — flag for split children
  isSplitChild: boolean = false;

  // Enemy trebuchet — attacks wall directly
  wallDamageOnly: boolean = false;

  // Boulder — crash/offload/fade state machine
  private boulderDoneAttack: boolean = false;
  private boulderOffloadCount: number = 0;
  private boulderOffloadTimer: number = 0;
  private boulderFading: boolean = false;
  private boulderFadeTimer: number = 1500;

  // Saboteur (Burrower) — immune to all damage while underground
  isBurrowed: boolean = false;

  // War Drummer — buffs nearby allies
  drumTimer: number = 0;
  private readonly drumInterval: number = 4000;
  private readonly drumBuffRadius: number = 200;
  drumPulseAlpha: number = 0; // visual pulse

  // Loot Goblin — steals gold, only damageable by clicks
  isGoblin: boolean = false;
  goblinStolenGold: number = 0;
  private goblinJitterTimer: number = 0;
  private goblinWanderTarget: { x: number; y: number } | null = null;
  private goblinWanderTimer: number = 0;

  // Siege Tower — deploys basic enemies at the wall
  siegeDeployTimer: number = 0;
  private readonly siegeDeployInterval: number = 4000;

  // Boss ability system
  bossAbility: string | null = null;
  abilityDescription?: string;
  private bossAbilityTimer: number = 0;
  private bossAbilityInterval: number = 0;
  bossAbilityCasting: boolean = false;       // True briefly when ability fires (visual cue)
  private bossAbilityCastTimer: number = 0;
  // Juggernaut: absorb shield
  bossShieldHits: number = 0;
  private bossShieldRechargeTimer: number = 0;
  private bossShieldRecharging: boolean = false;
  // Shadow: invulnerability window
  bossInvulTimer: number = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, config: EnemyConfig) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.enemyType = config.type || 'basic';
    this.hp = config.hp;
    this.maxHP = config.hp;
    this.baseSpeed = config.speed;
    this.speed = config.speed;
    this.damage = config.damage;
    this.attackInterval = config.attackInterval;
    this.goldReward = config.goldReward;
    this.attackRange = config.attackRange || 0;
    this.missChance = config.missChance || 0;
    this.rangedDamageReduction = config.rangedDamageReduction || 0;
    this.isBoss = config.isBoss || false;
    this.scale = config.scale || 1;
    // ...
    this.isAttacking = false;
    this.isDead = false;
    this.graphics = scene.add.graphics();
    this.graphics.setScale(this.scale);
    this.hpBarGfx = scene.add.graphics();
    this.hpBarGfx.setScale(this.scale);
    this.sprite = scene.add.sprite(x, y, `enemy_${this.enemyType}_walk_0`);
    this.sprite.setOrigin(0.5, 110 / 128);
    this.sprite.setScale(this.scale);
    this.walkPhase = Math.random() * Math.PI * 2;
    this.attackTimer = 0; // Ready to attack immediately
    this.hitFlashTimer = 0;
    this.slowTimer = 0;
    this.healTimer = 0;

    // Init swarm bees just in case
    for (let i = 0; i < 5; i++) {
      this.swarmBees.push({ x: 0, y: 0, angle: Math.random() * Math.PI * 2 });
    }

    // Shaman healing setup
    if (this.enemyType === 'shaman' && config.healPercent) {
      this.healPercent = config.healPercent;
    }

    // Nest carrier setup
    if (this.enemyType === 'nest_carrier') {
      this.nestSpawnTimer = this.nestSpawnInterval;
    }

    // Splitter child flag
    if (config.isSplitChild) {
      this.isSplitChild = true;
    }

    // Enemy trebuchet wall-damage flag
    if (config.wallDamageOnly) {
      this.wallDamageOnly = true;
    }

    // Boulder setup
    if (this.enemyType === 'boulder') {
      this.boulderOffloadCount = 4;
      this.boulderOffloadTimer = 0; // Timer starts after crash
    }

    // Saboteur setup — starts burrowed
    if (this.enemyType === 'saboteur') {
      this.isBurrowed = true;
    }

    // War Drummer setup
    if (this.enemyType === 'war_drummer') {
      this.drumTimer = this.drumInterval;
    }

    // Loot Goblin setup
    if (this.enemyType === 'loot_goblin') {
      this.isGoblin = true;
    }

    // Siege Tower setup
    if (this.enemyType === 'siege_tower') {
      this.siegeDeployTimer = this.siegeDeployInterval;
    }

    // Boss ability setup
    if (config.bossAbility) {
      this.bossAbility = config.bossAbility;
      this.abilityDescription = config.abilityDescription;
      const def = BOSS_DEFINITIONS.find(d => d.ability === config.bossAbility);
      this.bossAbilityInterval = def?.abilityInterval ?? 8000;
      this.bossAbilityTimer = this.bossAbilityInterval * 0.5; // First cast at half-interval
      if (config.bossAbility === 'juggernaut') {
        this.bossShieldHits = 8;
      }
    }
  }

  update(time: number, delta: number, wall: Wall, allEnemies?: Enemy[], enemyArrows?: EnemyArrow[]): void {
    if (this.isDead) return;

    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= delta;
    }

    // Boss ability timer
    if (this.isBoss && this.bossAbility) {
      // Casting visual timer
      if (this.bossAbilityCasting) {
        this.bossAbilityCastTimer -= delta;
        if (this.bossAbilityCastTimer <= 0) this.bossAbilityCasting = false;
      }

      // Juggernaut shield recharge
      if (this.bossShieldRecharging) {
        this.bossShieldRechargeTimer -= delta;
        if (this.bossShieldRechargeTimer <= 0) {
          this.bossShieldHits = 8;
          this.bossShieldRecharging = false;
        }
      }

      // Shadow invulnerability countdown
      if (this.bossInvulTimer > 0) {
        this.bossInvulTimer -= delta;
      }

      // Ability cooldown
      this.bossAbilityTimer -= delta;
      if (this.bossAbilityTimer <= 0) {
        this.bossAbilityTimer = this.bossAbilityInterval;
        this.bossAbilityCasting = true;
        this.bossAbilityCastTimer = 800;

        if (this.bossAbility === 'shadow') {
          // Shadow Stalker teleports 200px closer to wall, gains invulnerability
          const oldX = this.x;
          const oldY = this.y;
          const newX = Math.max(WALL_X + WALL_WIDTH + 250, this.x - 200);
          
          (this.scene as any).spawnShadowGhost?.(oldX, oldY); // Ghost at old pos
          this.spawnSmokeBurst(oldX, oldY); // Smoke at old pos
          
          this.x = newX;
          this.spawnSmokeBurst(newX, oldY); // Smoke at new pos
          
          if (!this.isAttacking) {
            const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
            if (this.x <= wallStopX) {
              this.isAttacking = true;
              this.x = wallStopX;
            }
          }
          this.bossInvulTimer = 1500;
        } else {
          // All other abilities route through GameScene
          (this.scene as any).onBossAbility?.(this.bossAbility, this);
        }
      }
    }

    // Handle slow effect expiration
    if (this.slowTimer > 0) {
      this.slowTimer -= delta;
      if (this.slowTimer <= 0) {
        this.slowMultiplier = 1;
        this.slowTimer = 0;
      }
    }

    // Handle swarm timer
    if (this.swarmTimer > 0) {
      this.swarmTimer -= delta;
      if (this.swarmTimer <= 0) this.isSwarmed = false;
    }

    // Handle stun timer (stunned = can't move or attack)
    if (this.stunTimer > 0) {
      this.stunTimer -= delta;
      this.draw();
      return; // Skip all movement and attack logic
    }

    // Handle disarm timer
    if (this.disarmTimer > 0) {
      this.disarmTimer -= delta;
    }

    // Calculate effective speed with slow
    const effectiveSpeed = this.baseSpeed * this.slowMultiplier;

    // Shaman: healer only — never attacks wall, roams seeking injured allies
    if (this.enemyType === 'shaman') {
      this.updateShaman(delta, effectiveSpeed, allEnemies || []);
    } else if (this.enemyType === 'saboteur') {
      // Saboteur: walk toward wall while burrowed (immune to all damage).
      // Once at melee range, emerge and fight normally.
      if (this.isBurrowed) {
        this.x -= effectiveSpeed * (delta / 1000);
        this.walkPhase += delta * 0.008; // Faster visual cycle for dirt mound
        const wallStopX = WALL_X + WALL_WIDTH + 20;
        if (this.x <= wallStopX) {
          this.isBurrowed = false;
          this.isAttacking = true;
          this.x = wallStopX;
          // Visual: dirt burst on emerge
          const burst = this.scene.add.graphics();
          burst.setPosition(this.x, this.y);
          for (let i = 0; i < 6; i++) {
            const ox = (Math.random() - 0.5) * 24;
            const oy = (Math.random() - 0.5) * 16;
            burst.fillStyle(0x8B6914, 0.8);
            burst.fillCircle(ox, oy, 4 + Math.random() * 4);
          }
          this.scene.tweens.add({
            targets: burst,
            alpha: 0, y: this.y - 20,
            duration: 500,
            onComplete: () => burst.destroy()
          });
        }
      } else {
        // Emerged: standard melee attack logic
        this.attackTimer -= delta;
        const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
        const atWall = this.x <= wallStopX + 5;
        if (this.attackTimer <= 0 && this.disarmTimer <= 0 && atWall) {
          this.attackTimer = this.attackInterval;
          wall.takeDamage(this.damage);
        }
      }
    } else if (this.enemyType === 'war_drummer') {
      // War Drummer: walks slowly, periodically buffs all nearby enemies
      if (!this.isAttacking) {
        this.x -= effectiveSpeed * (delta / 1000);
        this.walkPhase += delta * 0.003;
        const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
        if (this.x <= wallStopX) {
          this.isAttacking = true;
          this.x = wallStopX;
        }
      } else {
        this.attackTimer -= delta;
        const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
        const atWall = this.x <= wallStopX + 5;
        if (this.attackTimer <= 0 && this.disarmTimer <= 0 && atWall) {
          this.attackTimer = this.attackInterval;
          wall.takeDamage(this.damage);
        }
      }
      // Drum pulse: buff nearby allies
      this.drumTimer -= delta;
      if (this.drumPulseAlpha > 0) this.drumPulseAlpha -= delta * 0.002;
      if (this.drumTimer <= 0) {
        this.drumTimer = this.drumInterval;
        this.drumPulseAlpha = 1;
        if (allEnemies) {
          for (const ally of allEnemies) {
            if (ally === this || ally.isDead) continue;
            const dx = ally.x - this.x;
            const dy = ally.y - this.y;
            if (Math.sqrt(dx * dx + dy * dy) <= this.drumBuffRadius) {
              ally.applySlow(2.0, 3000); // Speed up (slowMultiplier > 1 = faster)
              ally.attackInterval = Math.max(200, ally.attackInterval * 0.5);
            }
          }
        }
      }
    } else if (this.enemyType === 'loot_goblin') {
      // Loot Goblin: runs around the whole field, steals gold each tick, never stops at wall

      // Y jitter for erratic feel
      this.goblinJitterTimer += delta;
      if (this.goblinJitterTimer > 80) {
        this.goblinJitterTimer = 0;
        this.y += (Math.random() - 0.5) * 8;
        this.y = Math.max(ENEMY_MIN_Y, Math.min(ENEMY_MAX_Y, this.y));
      }

      // Pick a new wander target when needed
      this.goblinWanderTimer -= delta;
      if (!this.goblinWanderTarget || this.goblinWanderTimer <= 0) {
        // Roam anywhere between just past the wall and the right edge of the field
        const minX = WALL_X + WALL_WIDTH + 60;
        const maxX = GAME_WIDTH - 60;
        this.goblinWanderTarget = {
          x: minX + Math.random() * (maxX - minX),
          y: ENEMY_MIN_Y + Math.random() * (ENEMY_MAX_Y - ENEMY_MIN_Y),
        };
        this.goblinWanderTimer = 1800 + Math.random() * 2200;
      }

      // Move toward wander target
      const gdx = this.goblinWanderTarget.x - this.x;
      const gdy = this.goblinWanderTarget.y - this.y;
      const gdist = Math.sqrt(gdx * gdx + gdy * gdy);
      if (gdist > 12) {
        const move = effectiveSpeed * 1.3 * (delta / 1000); // Slightly faster than base speed
        this.x += (gdx / gdist) * move;
        this.y += (gdy / gdist) * move;
        this.walkPhase += delta * 0.01;
      } else {
        // Reached target — invalidate so a new one is picked next frame
        this.goblinWanderTarget = null;
      }

      // Steal gold periodically from anywhere on the field
      this.attackTimer -= delta;
      if (this.attackTimer <= 0) {
        this.attackTimer = this.attackInterval;
        const gameScene = this.scene as any;
        if (gameScene.economyManager) {
          const currentGold = gameScene.economyManager.gold;
          const stealAmount = Math.floor(currentGold * 0.02); // Steal 2% of current gold
          if (stealAmount > 0) {
            gameScene.economyManager.gold = Math.max(0, currentGold - stealAmount);
            this.goblinStolenGold += stealAmount;
            gameScene.floatingTextManager?.spawn(this.x, this.y - 20, `-${stealAmount}g`, 'damage');
          }
        }
      }
    } else if (this.enemyType === 'siege_tower') {
      // Siege Tower: walks very slowly, deploys basic enemies periodically
      if (!this.isAttacking) {
        this.x -= effectiveSpeed * (delta / 1000);
        this.walkPhase += delta * 0.002;
        const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
        if (this.x <= wallStopX) {
          this.isAttacking = true;
          this.x = wallStopX;
        }
      }
      // Deploy basic enemies whether or not we've reached the wall
      this.siegeDeployTimer -= delta;
      if (this.siegeDeployTimer <= 0) {
        this.siegeDeployTimer = this.siegeDeployInterval;
        const gameScene = this.scene as any;
        if (gameScene.spawnSiegeTowerChild) {
          gameScene.spawnSiegeTowerChild(this);
        }
      }
    } else if (this.enemyType === 'boulder' && this.isAttacking) {
      // Phase 2: Crash on first wall contact
      if (!this.boulderDoneAttack) {
        this.boulderDoneAttack = true;
        wall.takeDamage(this.damage);
        this.boulderOffloadTimer = 400; // First minion after 400ms
      }

      // Phase 3: Offload minions every 400ms
      if (this.boulderOffloadCount > 0) {
        this.boulderOffloadTimer -= delta;
        if (this.boulderOffloadTimer <= 0) {
          this.boulderOffloadCount--;
          this.boulderOffloadTimer = 400;
          (this.scene as any).spawnBoulderChild?.(this);
        }
      }

      // Phase 4: Begin fading when all minions have been offloaded
      if (this.boulderDoneAttack && this.boulderOffloadCount <= 0) {
        this.boulderFading = true;
      }

      // Position and draw
      this.graphics.setPosition(this.x, this.y);
      this.hpBarGfx.setPosition(this.x, this.y);
      this.draw();

      // Apply fade alpha after draw so it persists
      if (this.boulderFading) {
        this.boulderFadeTimer -= delta;
        const alpha = Math.max(0, this.boulderFadeTimer / 1500);
        this.sprite.setAlpha(alpha);
        this.graphics.setAlpha(alpha);
        this.hpBarGfx.setAlpha(alpha);
        if (this.boulderFadeTimer <= 0) {
          this.goldReward = 0;
          this.isDead = true;
          this.destroy();
          return;
        }
      }
      return; // Skip standard attack/movement and DoT logic
    } else if (!this.isAttacking) {
      // Walk toward wall
      this.x -= effectiveSpeed * (delta / 1000);
      this.walkPhase += delta * 0.005;

      // Stop position depends on attack range
      const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
      if (this.x <= wallStopX) {
        this.isAttacking = true;
        this.x = wallStopX;
      }
    } else {
      // Attack wall — only if enemy is actually at wall range (not blocked by bramble)
      const wallStopX = WALL_X + WALL_WIDTH + 20 + this.attackRange;
      const atWall = this.x <= wallStopX + 5;

      // Mason "Thorny Walls" — enemies take damage while attacking at the wall
      if (atWall) {
        const gameScene = this.scene as any;
        const thornyPct = gameScene.elderBonuses?.thornyWallPercent ?? 0;
        if (thornyPct > 0) {
          const thornyDmg = this.maxHP * thornyPct * (delta / 1000);
          this.takeDamage(thornyDmg);
        }
      }

      if (this.disarmTimer > 0) {
        this.draw();
        // Still count down poison/etc below, but skip attack
      }
      this.attackTimer -= delta;
      if (this.attackTimer <= 0 && this.disarmTimer <= 0 && atWall) {
        this.attackTimer = this.attackInterval;

        // Calculate total miss chance (base accuracy + swarm penalty)
        let totalMissChance = this.missChance;
        if (this.isSwarmed) totalMissChance += 0.5; // Swarm adds 50% miss
        totalMissChance = Math.min(totalMissChance, 0.9); // Cap at 90%

        const isMiss = totalMissChance > 0 && Math.random() < totalMissChance;

        if ((this.enemyType === 'archer' || this.enemyType === 'runner' || this.enemyType === 'longbow_archer' || this.enemyType === 'enemy_trebuchet') && enemyArrows) {
          // Fire a real projectile toward the wall
          const projectileType = this.enemyType === 'runner' ? 'sling_stone' as const : (this.enemyType === 'enemy_trebuchet' ? 'rock' as const : 'arrow' as const);
          const wallTargetX = WALL_X + WALL_WIDTH / 2;
          let targetY = this.y + (Math.random() - 0.5) * 20;
          if (isMiss) {
            // Miss: offset target significantly so projectile flies past
            targetY += (Math.random() > 0.5 ? 1 : -1) * (60 + Math.random() * 40);
            // Show Miss text
            const text = this.scene.add.text(this.x, this.y - 40, "Miss!", {
              fontSize: '14px',
              color: '#ff0000',
              stroke: '#000000',
              strokeThickness: 2
            }).setOrigin(0.5);
            this.scene.tweens.add({
              targets: text,
              y: this.y - 70,
              alpha: 0,
              duration: 800,
              onComplete: () => text.destroy()
            });
          }
          const arrow = new EnemyArrow(
            this.scene, this.x - 5, this.y - 45, // Trebuchets launch from higher up
            wallTargetX, targetY,
            isMiss ? 0 : this.damage,
            projectileType
          );
          enemyArrows.push(arrow);
        } else if (isMiss) {
          // Show Miss text for non-archer ranged enemies
          const text = this.scene.add.text(this.x, this.y - 40, "Miss!", {
            fontSize: '14px',
            color: '#ff0000',
            stroke: '#000000',
            strokeThickness: 2
          }).setOrigin(0.5);
          this.scene.tweens.add({
            targets: text,
            y: this.y - 70,
            alpha: 0,
            duration: 800,
            onComplete: () => text.destroy()
          });
        } else {
          wall.takeDamage(this.damage);
        }
      }
    }

    // Handle poison DoT
    if (this.poisonTimer > 0) {
      this.poisonTimer -= delta;
      this.poisonTickTimer += delta;
      if (this.poisonTickTimer >= 500) { // Tick every 500ms
        const ticks = Math.floor(this.poisonTickTimer / 500);
        const dmg = Math.round(this.poisonDps * ticks * 0.5);
        if (dmg > 0) {
          this.hp -= dmg;
          (this.scene as any).floatingTextManager?.spawn(this.x, this.y - 30, Math.floor(dmg).toString(), 'damage');
          this.hitFlashTimer = 50;
          if (this.hp <= 0) {
            this.isDead = true;
            this.destroy();
            return;
          }
        }
        this.poisonTickTimer %= 500;
      }
      if (this.poisonTimer <= 0) {
        this.poisonDps = 0;
        this.poisonTimer = 0;
        this.poisonTickTimer = 0;
      }
    }

    // Handle fire DoT
    if (this.fireTimer > 0) {
      this.fireTimer -= delta;
      this.fireTickTimer += delta;
      if (this.fireTickTimer >= 500) { // Tick every 500ms
        const ticks = Math.floor(this.fireTickTimer / 500);
        const dmg = Math.round(this.fireDps * ticks * 0.5);
        if (dmg > 0) {
          this.hp -= dmg;
          (this.scene as any).floatingTextManager?.spawn(this.x + (Math.random() * 10 - 5), this.y - 40, Math.floor(dmg).toString(), 'damage');
          this.hitFlashTimer = 50;
          if (this.hp <= 0) {
            this.isDead = true;
            this.destroy();
            return;
          }
        }
        this.fireTickTimer %= 500;
      }
      if (this.fireTimer <= 0) {
        this.fireDps = 0;
        this.fireTimer = 0;
        this.fireTickTimer = 0;
      }
    }

    // Nest carrier: periodically request child swarmling spawn
    if (this.enemyType === 'nest_carrier' && this.nestChildCount < Enemy.NEST_MAX_CHILDREN) {
      this.nestSpawnTimer -= delta;
      if (this.nestSpawnTimer <= 0) {
        this.nestSpawnTimer = this.nestSpawnInterval;
        // Signal GameScene to spawn a swarmling at our position
        const gameScene = this.scene as any;
        if (gameScene.spawnNestChild) {
          gameScene.spawnNestChild(this);
        }
      }
    }

    if (this.isDead) return;

    this.graphics.setPosition(this.x, this.y);
    this.hpBarGfx.setPosition(this.x, this.y);
    this.draw();
  }

  healNearbyAllies(allEnemies: Enemy[]): void {
    const healRange = ENEMY_TYPES.shaman.healRange;
    for (const enemy of allEnemies) {
      if (enemy === this || enemy.isDead) continue;
      if (enemy.hp >= enemy.maxHP) continue;
      const dx = enemy.x - this.x;
      const dy = enemy.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= healRange) {
        const healAmount = Math.round(enemy.maxHP * this.healPercent);
        if (healAmount <= 0) continue;
        enemy.hp = Math.min(enemy.hp + healAmount, enemy.maxHP);
        // Show "+" heal text on healed unit
        const healText = this.scene.add.text(enemy.x, enemy.y - 40, `+${healAmount}`, {
          fontSize: '11px',
          fontFamily: 'Arial, sans-serif',
          color: '#44ff44',
          stroke: '#000000',
          strokeThickness: 2,
        }).setOrigin(0.5);
        this.scene.tweens.add({
          targets: healText,
          y: enemy.y - 60,
          alpha: 0,
          duration: 600,
          onComplete: () => healText.destroy(),
        });
      }
    }
  }

  private updateShaman(delta: number, effectiveSpeed: number, allEnemies: Enemy[]): void {
    const healRange = ENEMY_TYPES.shaman.healRange;

    // Find most injured ally
    let mostInjured: Enemy | null = null;
    let worstHpRatio = 1;
    for (const enemy of allEnemies) {
      if (enemy === this || enemy.isDead) continue;
      if (enemy.hp >= enemy.maxHP) continue;
      const hpRatio = enemy.hp / enemy.maxHP;
      if (hpRatio < worstHpRatio) {
        worstHpRatio = hpRatio;
        mostInjured = enemy;
      }
    }

    const moveSpeed = effectiveSpeed * 0.7;

    if (mostInjured) {
      // Move toward injured ally
      const dx = mostInjured.x - this.x;
      const dy = mostInjured.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > healRange * 0.6) {
        const moveAmount = moveSpeed * (delta / 1000);
        this.x += (dx / dist) * moveAmount;
        this.y += (dy / dist) * moveAmount;
      }
      this.walkPhase += delta * 0.005;
      this.wanderTarget = null;
    } else {
      // No one to heal — wander around the back of the map
      const backZoneMinX = GAME_WIDTH * 0.6;
      const backZoneMaxX = GAME_WIDTH - 40;

      // Pick a new wander target every few seconds
      this.wanderTimer -= delta;
      if (!this.wanderTarget || this.wanderTimer <= 0) {
        this.wanderTarget = {
          x: backZoneMinX + Math.random() * (backZoneMaxX - backZoneMinX),
          y: ENEMY_MIN_Y + Math.random() * (ENEMY_MAX_Y - ENEMY_MIN_Y),
        };
        this.wanderTimer = 2000 + Math.random() * 3000;
      }

      // Move toward wander target
      const dx = this.wanderTarget.x - this.x;
      const dy = this.wanderTarget.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 10) {
        const moveAmount = moveSpeed * 0.5 * (delta / 1000);
        this.x += (dx / dist) * moveAmount;
        this.y += (dy / dist) * moveAmount;
        this.walkPhase += delta * 0.003;
      }
    }

    // Heal nearby allies
    if (this.healPercent > 0) {
      this.healTimer -= delta;
      if (this.healTimer <= 0) {
        this.healTimer = 1000;
        this.healNearbyAllies(allEnemies);
      }
    }
  }

  applySlow(multiplier: number, duration: number): void {
    this.slowMultiplier = multiplier;
    this.slowTimer = duration; // Set timer directly
  }

  applyPoison(dps: number, duration: number): void {
    // Refresh poison — take the stronger effect
    if (dps > this.poisonDps || this.poisonTimer <= 0) {
      this.poisonDps = dps;
    }
    this.poisonTimer = duration;
  }

  applyFire(dps: number, duration: number): void {
    // Refresh fire — take the stronger effect
    if (dps > this.fireDps || this.fireTimer <= 0) {
      this.fireDps = dps;
    }
    this.fireTimer = duration;
  }

  applySwarm(duration: number): void {
    this.isSwarmed = true;
    this.swarmTimer = duration;
  }

  applyStun(duration: number): void {
    this.stunTimer = Math.max(this.stunTimer, duration);
  }

  applyDisarm(duration: number): void {
    this.disarmTimer = Math.max(this.disarmTimer, duration);
  }

  applyKnockback(distance: number): void {
    // Push enemy back (to the right, away from wall)
    this.x += distance;
    // If enemy was attacking, un-stick them so they walk back
    if (this.isAttacking) {
      this.isAttacking = false;
    }
  }

  private spawnSmokeBurst(x: number, y: number): void {
    const smoke = this.scene.add.graphics();
    smoke.setPosition(x, y - 20);
    
    // Draw an expanding cloud of dark purple smoke
    for (let i = 0; i < 5; i++) {
      const offsetX = (Math.random() - 0.5) * 30;
      const offsetY = (Math.random() - 0.5) * 30;
      const radius = 15 + Math.random() * 15;
      
      smoke.fillStyle(0x660099, 0.7);
      smoke.fillCircle(offsetX, offsetY, radius);
      smoke.fillStyle(0x9900cc, 0.4);
      smoke.fillCircle(offsetX + 5, offsetY - 5, radius * 0.6);
    }
    
    // Burst scale and fade
    smoke.setScale(0.2);
    this.scene.tweens.add({
      targets: smoke,
      scale: 1.5,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onComplete: () => smoke.destroy()
    });
  }

  private spawnMetallicSpark(x: number, y: number): void {
    const spark = this.scene.add.graphics();
    spark.setPosition(x, y);
    
    // Draw an impact star
    spark.lineStyle(2, 0xffffff, 1);
    spark.fillStyle(0xffffaa, 1);
    
    spark.beginPath();
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      const length = i % 2 === 0 ? 15 : 6;
      const px = Math.cos(angle) * length;
      const py = Math.sin(angle) * length;
      if (i === 0) spark.moveTo(px, py);
      else spark.lineTo(px, py);
    }
    spark.closePath();
    spark.fillPath();
    spark.strokePath();

    // Random rotation and scale for punchiness
    spark.setRotation(Math.random() * Math.PI);
    spark.setScale(0.5);

    this.scene.tweens.add({
      targets: spark,
      scale: 1.2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => spark.destroy()
    });
  }

  takeDamage(amount: number, isRanged: boolean = false, isClick: boolean = false): boolean {
    // Boulder is invulnerable — can only be removed by its own fade-out
    if (this.enemyType === 'boulder') return false;

    // Saboteur: completely immune while burrowed
    if (this.isBurrowed) return false;

    // Loot Goblin: only damageable by clicks
    if (this.isGoblin && !isClick) return false;

    // Shadow Stalker: invulnerable during teleport window
    if (this.bossInvulTimer > 0) {
      this.hitFlashTimer = 30; // Brief visual cue that damage was blocked
      (this.scene as any).floatingTextManager?.spawn(this.x, this.y - 40, 'BLOCKED', 'damage');
      return false;
    }

    // Iron Juggernaut: absorb shield blocks hit
    if (this.bossAbility === 'juggernaut' && this.bossShieldHits > 0) {
      this.bossShieldHits--;
      this.hitFlashTimer = 80;
      
      // Spark visual on shield hit
      this.spawnMetallicSpark(this.x, this.y - 20);
      
      if (this.bossShieldHits === 0 && !this.bossShieldRecharging) {
        this.bossShieldRecharging = true;
        this.bossShieldRechargeTimer = this.bossAbilityInterval;
        (this.scene as any).floatingTextManager?.spawn(this.x, this.y - 70, 'SHIELD BROKEN!', 'damage');
      }
      return false;
    }

    let finalDamage = amount;
    if (isRanged && this.rangedDamageReduction > 0) {
      finalDamage = amount * (1 - this.rangedDamageReduction);
    }
    this.hp -= finalDamage;
    this.hitFlashTimer = 100;
    if (this.hp <= 0) {
      this.isDead = true;
      this.destroy();
      return true;
    }
    return false;
  }

  draw(): void {
    // Determine color
    const color = this.hitFlashTimer > 0 ? COLORS.enemyHit : COLORS.enemy;

    // Position graphics containers at enemy location
    this.graphics.setPosition(this.x, this.y);
    this.hpBarGfx.setPosition(this.x, this.y);
    this.sprite.setPosition(this.x, this.y);

    // Update sprite texture
    const stateSuffix = this.hitFlashTimer > 0 ? '_hit' : '';
    let phaseNorm = (this.walkPhase % (Math.PI * 2)) / (Math.PI * 2);
    if (phaseNorm < 0) phaseNorm += 1;
    let frame = Math.floor(phaseNorm * 8);
    if (frame > 7) frame = 7;

    const texKey = this.isAttacking
      ? `enemy_${this.enemyType}_attack${stateSuffix}`
      : `enemy_${this.enemyType}_walk${stateSuffix}_${frame}`;

    this.sprite.setTexture(texKey);

    // Clear previous frame for overlay graphics
    this.graphics.clear();
    this.hpBarGfx.clear();

    // Custom overlay drawings for certain types
    if (this.enemyType === 'shaman') {
      // Permanent green healing radius indicator
      const healRange = ENEMY_TYPES.shaman.healRange;
      this.graphics.lineStyle(1, 0x44ff44, 0.3);
      this.graphics.strokeCircle(0, -15, healRange);
      this.graphics.fillStyle(0x44ff44, 0.05);
      this.graphics.fillCircle(0, -15, healRange);
    }

    // Boss ability visual overlays
    if (this.isBoss && this.bossAbility) {
      const t = this.scene.time.now;
      const pulse = Math.sin(t * 0.004);

      if (this.bossAbility === 'frost') {
        // Icy blue glow
        this.graphics.fillStyle(0x88ddff, 0.12 + 0.06 * pulse);
        this.graphics.fillCircle(0, -20, 45);
        // Orbiting snowflake dots (6)
        for (let i = 0; i < 6; i++) {
          const angle = (t * 0.0015) + (i * Math.PI * 2 / 6);
          const bx = Math.cos(angle) * 32;
          const by = -20 + Math.sin(angle) * 18;
          this.graphics.fillStyle(0xccf0ff, 0.9);
          this.graphics.fillRect(bx - 2, by - 2, 4, 4);
          // Inner dot
          this.graphics.fillStyle(0xffffff, 1);
          this.graphics.fillRect(bx - 1, by - 1, 2, 2);
        }
        // Casting ring: expanding cyan circle burst
        if (this.bossAbilityCasting) {
          const progress = 1 - (this.bossAbilityCastTimer / 800);
          const radius = 20 + progress * 80;
          this.graphics.lineStyle(3, 0x00ddff, (1 - progress) * 0.9);
          this.graphics.strokeCircle(0, -20, radius);
        }

      } else if (this.bossAbility === 'lava') {
        // Orange/red glow
        this.graphics.fillStyle(0xff4400, 0.18 + 0.08 * pulse);
        this.graphics.fillCircle(0, -20, 48);
        // Dripping lava drops
        for (let i = 0; i < 4; i++) {
          const dropPhase = ((t * 0.001 + i * 0.8) % 1.0);
          const dx = (i - 1.5) * 12;
          const dy = -5 + dropPhase * 30;
          const alpha = dropPhase < 0.7 ? 1 : (1 - (dropPhase - 0.7) / 0.3);
          this.graphics.fillStyle(0xff6600, alpha * 0.9);
          this.graphics.fillCircle(dx, dy, 5 - dropPhase * 2);
        }
        // Casting: orange explosion ring
        if (this.bossAbilityCasting) {
          const progress = 1 - (this.bossAbilityCastTimer / 800);
          const radius = 15 + progress * 60;
          this.graphics.lineStyle(4, 0xff6600, (1 - progress) * 0.8);
          this.graphics.strokeCircle(0, -20, radius);
          this.graphics.fillStyle(0xff4400, (1 - progress) * 0.3);
          this.graphics.fillCircle(0, -20, radius * 0.5);
        }

      } else if (this.bossAbility === 'storm') {
        // Yellow glow
        this.graphics.fillStyle(0xffee00, 0.12 + 0.05 * pulse);
        this.graphics.fillCircle(0, -20, 40);
        // Lightning sparks (4 jagged short lines)
        for (let i = 0; i < 4; i++) {
          const angle = (t * 0.003 * (i % 2 === 0 ? 1 : -1)) + (i * Math.PI / 2);
          const r1 = 14, r2 = 30 + (i % 2) * 8;
          const x1 = Math.cos(angle) * r1;
          const y1 = -20 + Math.sin(angle) * r1;
          const mx = Math.cos(angle + 0.4) * ((r1 + r2) * 0.5);
          const my = -20 + Math.sin(angle + 0.4) * ((r1 + r2) * 0.5);
          const x2 = Math.cos(angle) * r2;
          const y2 = -20 + Math.sin(angle) * r2;
          this.graphics.lineStyle(2, 0xffff44, 0.8);
          this.graphics.beginPath();
          this.graphics.moveTo(x1, y1);
          this.graphics.lineTo(mx, my);
          this.graphics.lineTo(x2, y2);
          this.graphics.strokePath();
        }
        // Casting: full bright flash
        if (this.bossAbilityCasting) {
          const progress = 1 - (this.bossAbilityCastTimer / 800);
          this.graphics.fillStyle(0xffffff, (1 - progress) * 0.5);
          this.graphics.fillCircle(0, -20, 55);
          this.graphics.lineStyle(3, 0xffee00, (1 - progress));
          this.graphics.strokeCircle(0, -20, 20 + progress * 70);
        }

      } else if (this.bossAbility === 'shadow') {
        // Dark purple smoke wisps
        this.graphics.fillStyle(0x660099, 0.18 + 0.06 * pulse);
        this.graphics.fillCircle(0, -20, 38);
        // Scattered purple smoke particles
        for (let i = 0; i < 8; i++) {
          const angle = (t * 0.0008 * (i % 2 === 0 ? 1 : -1.3)) + (i * 0.8);
          const r = 20 + (i % 3) * 8;
          const px = Math.cos(angle) * r;
          const py = -20 + Math.sin(angle) * r * 0.5;
          const sz = 3 + (i % 3);
          this.graphics.fillStyle(0x9900cc, 0.5 + 0.3 * Math.sin(t * 0.005 + i));
          this.graphics.fillCircle(px, py, sz);
        }
        // Invulnerable: sprite goes semi-transparent + purple shimmer
        if (this.bossInvulTimer > 0) {
          this.sprite.setAlpha(0.3);
          this.graphics.fillStyle(0xcc00ff, 0.25 + 0.15 * pulse);
          this.graphics.fillCircle(0, -20, 42);
        } else {
          this.sprite.setAlpha(0.88);
        }

      } else if (this.bossAbility === 'plague') {
        // Sickly green bubbling aura
        this.graphics.fillStyle(0x22aa22, 0.15 + 0.08 * pulse);
        this.graphics.fillCircle(0, -20, 44);
        // Orbiting skull-like circles (green bubbles)
        for (let i = 0; i < 5; i++) {
          const angle = (t * 0.0012) + (i * Math.PI * 2 / 5);
          const r = 26 + Math.sin(t * 0.003 + i) * 4;
          const bx = Math.cos(angle) * r;
          const by = -20 + Math.sin(angle) * r * 0.6;
          const sz = 4 + (i % 2) * 2;
          this.graphics.fillStyle(0x44ff44, 0.7);
          this.graphics.fillCircle(bx, by, sz);
          // Eyes on each bubble
          this.graphics.fillStyle(0x002200, 0.9);
          this.graphics.fillRect(bx - 2, by - 1, 1, 1);
          this.graphics.fillRect(bx + 1, by - 1, 1, 1);
        }
        // Casting: green cloud burst
        if (this.bossAbilityCasting) {
          const progress = 1 - (this.bossAbilityCastTimer / 800);
          const radius = 18 + progress * 75;
          this.graphics.lineStyle(3, 0x44ff44, (1 - progress) * 0.8);
          this.graphics.strokeCircle(0, -20, radius);
          this.graphics.fillStyle(0x22cc22, (1 - progress) * 0.2);
          this.graphics.fillCircle(0, -20, radius);
        }

      } else if (this.bossAbility === 'juggernaut') {
        // Gray metallic ring
        this.graphics.lineStyle(3, 0xaaaaaa, 0.6 + 0.2 * pulse);
        this.graphics.strokeCircle(0, -20, 42);
        // Orbiting armor plates — one per remaining shield hit
        const shieldHits = this.bossShieldHits;
        for (let i = 0; i < shieldHits; i++) {
          const angle = (t * 0.0005) + (i * Math.PI * 2 / 8);
          const r = 36;
          const px = Math.cos(angle) * r;
          const py = -20 + Math.sin(angle) * r * 0.5;
          this.graphics.fillStyle(0xcccccc, 0.9);
          this.graphics.fillRect(px - 5, py - 3, 10, 6);
          this.graphics.lineStyle(1, 0x888888, 0.8);
          this.graphics.strokeRect(px - 5, py - 3, 10, 6);
        }
        
        // Juggernaut metallic tint logic based on shield
        if (shieldHits > 0) {
          // Keep a strong metallic gray tint while armored
          this.sprite.setTint(0x9999bb);
        } else {
          // No shield: red exposed glow, remove metallic tint
          this.sprite.clearTint();
          this.graphics.fillStyle(0xff2200, 0.15 + 0.1 * pulse);
          this.graphics.fillCircle(0, -20, 46);
        }
      }
    }

    // War Drummer: pulsing red buff aura
    if (this.enemyType === 'war_drummer') {
      const alpha = Math.max(0, this.drumPulseAlpha);
      if (alpha > 0) {
        this.graphics.lineStyle(3, 0xff4444, alpha * 0.8);
        this.graphics.strokeCircle(0, -15, this.drumBuffRadius * alpha);
        this.graphics.fillStyle(0xff4444, alpha * 0.1);
        this.graphics.fillCircle(0, -15, this.drumBuffRadius * alpha);
      }
      // Permanent subtle circle showing buff radius
      this.graphics.lineStyle(1, 0xff4444, 0.15);
      this.graphics.strokeCircle(0, -15, this.drumBuffRadius);
    }

    // Loot Goblin: gold shimmer around
    if (this.enemyType === 'loot_goblin') {
      const t = this.scene.time.now;
      for (let i = 0; i < 3; i++) {
        const sparkAngle = (t * 0.003 + i * 2.1) % (Math.PI * 2);
        const sx = Math.cos(sparkAngle) * 12;
        const sy = -15 + Math.sin(sparkAngle) * 8;
        this.graphics.fillStyle(0xffd700, 0.6 + Math.sin(t * 0.005 + i) * 0.4);
        this.graphics.fillRect(sx - 1, sy - 1, 2, 2);
      }
      // Show stolen gold indicator
      if (this.goblinStolenGold > 0) {
        this.graphics.fillStyle(0xffd700, 0.3);
        this.graphics.fillCircle(0, -20, 8 + Math.min(this.goblinStolenGold / 500, 6));
      }
    }

    // Saboteur: dirt trail particles while burrowed
    if (this.enemyType === 'saboteur' && this.isBurrowed) {
      const t = this.scene.time.now;
      for (let i = 0; i < 3; i++) {
        const offset = ((t * 0.001 + i * 0.5) % 1.5) * 20;
        this.graphics.fillStyle(0x8B6914, 0.3);
        this.graphics.fillCircle(offset, (Math.random() - 0.5) * 6, 2);
      }
    }

    // HP bar above head (hide for boss as it has main UI bar)
    if (this.hp < this.maxHP && !this.isBoss) {
      const barW = this.enemyType === 'brute' ? 28 : 20;
      const barH = 3;
      const barX = -barW / 2;
      const headOffset = this.enemyType === 'brute' ? 50 : 40;
      const barY = -headOffset;
      this.hpBarGfx.fillStyle(COLORS.hpBarRed, 1);
      this.hpBarGfx.fillRect(barX, barY, barW, barH);
      this.hpBarGfx.fillStyle(COLORS.hpBarGreen, 1);
      this.hpBarGfx.fillRect(barX, barY, barW * (this.hp / this.maxHP), barH);
    }

    // Draw poison indicator
    if (this.poisonTimer > 0) {
      this.graphics.fillStyle(0x44cc44, 0.25);
      this.graphics.fillCircle(0, -20, 12);
    }

    // Draw fire indicator — flickering orange glow
    if (this.fireTimer > 0) {
      const flicker = Math.random() * 4;
      this.graphics.fillStyle(0xff6600, 0.3);
      this.graphics.fillCircle(0, -20, 10 + flicker);
      this.graphics.fillStyle(0xffaa00, 0.2);
      this.graphics.fillCircle(Math.random() * 6 - 3, -25 - Math.random() * 5, 4 + flicker);
      this.graphics.fillStyle(0xff4400, 0.25);
      this.graphics.fillCircle(Math.random() * 4 - 2, -15 - Math.random() * 3, 3 + flicker);
    }

    // Draw swarm bees if swarmed
    if (this.isSwarmed) {
      this.graphics.fillStyle(0xffff00, 1); // Yellow bees
      for (const bee of this.swarmBees) {
        bee.angle += 0.1;
        const bx = Math.cos(bee.angle) * 15;
        const by = -30 + Math.sin(bee.angle) * 6; // Orbit head roughly
        this.graphics.fillRect(bx, by, 2, 2);
        // Trail or black body?
        this.graphics.fillStyle(0x000000, 1);
        this.graphics.fillRect(bx + 1, by, 1, 1);
        this.graphics.fillStyle(0xffff00, 1);
      }
    }
  }

  destroy(): void {
    this.graphics.destroy();
    this.hpBarGfx.destroy();
    this.sprite.destroy();
  }

  getBounds(): { x: number; y: number; width: number; height: number } {
    const size = (this.enemyType === 'brute' ? 1.3 : (this.enemyType === 'runner' ? 0.8 : (this.enemyType === 'shieldbearer' ? 1.1 : (this.enemyType === 'longbow_archer' ? 1.1 : 1)))) * this.scale;
    return { x: this.x - 8 * size, y: this.y - 36 * size, width: 16 * size, height: 36 * size };
  }
}
